import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  Zap, Target, Shield, Users, GitBranch, BarChart3,
  Shuffle, Link as LinkIcon, Mail, Store, Check, ArrowRight,
} from "lucide-react";

const SECTIONS = [
  {
    id: "ai", label: "AI Suite", color: "coral", icon: Zap,
    features: [
      { title: "Campaign Builder Wizard",   desc: "3-step wizard: describe your goal, preview the AI-written subject and body, save. Generates subject line, preview text, full HTML body, and a CTA button.", href: "/ai-campaign-builder" },
      { title: "AI Campaign Generator",     desc: "Generate complete multi-step email sequences from a single text prompt. Supports welcome sequences, product launches, drip campaigns, and more — with personalization tokens included automatically.", href: "/ai-campaign-generator" },
      { title: "Subject Line Optimisation", desc: "AI rewrites your subject lines using open-rate data from previous sends. A/B test up to 4 variants and let MoreMail pick the winner automatically.", href: "/subject-line-optimisation" },
      { title: "Spam Score",               desc: "Before sending, every campaign is scored 1-10 for spam risk. See exactly which phrases to change before they cost you inbox placement.", href: "/spam-score" },
      { title: "Inbox Preview",            desc: "Preview your email exactly as it appears in Gmail desktop, Gmail mobile, and Outlook — including dark mode rendering.", href: "/inbox-preview" },
    ],
  },
  {
    id: "deliverability", label: "Deliverability", color: "green", icon: Shield,
    features: [
      { title: "Email Validation",          desc: "Free MX record check on every subscriber import. Optional ZeroBounce deep validation (200/day free). Invalid addresses never get sent to — protecting your sender reputation." },
      { title: "Warmup Schedule",           desc: "Automatic daily send cap that ramps from 20 to 2,000 emails over 30 days. Prevents sudden spikes that trigger spam filters on new domains." },
      { title: "Cross-User Warmup Pool",    desc: "MoreMail users warm each other's accounts. AI-generated emails flow between pool members and get marked as not-spam via IMAP — building real reputation with Gmail, Outlook, and Yahoo." },
      { title: "IP Blacklist Monitoring",   desc: "Your sending IP is checked daily against 10 free DNS blacklists including Spamhaus ZEN and SpamCop. Get alerted the moment you're listed, with direct delist links." },
      { title: "Custom Tracking Domains",   desc: "Point your own subdomain (e.g. track.yourdomain.com) at MoreMail's tracking server. Open and click tracking links use your domain instead of a shared platform domain." },
      { title: "List Hygiene",              desc: "Automatically unsubscribe chronically inactive subscribers to improve deliverability, reduce costs, and keep engagement metrics accurate. Configurable sunset policy with full audit logs.", href: "/list-hygiene" },
    ],
  },
  {
    id: "leads", label: "Lead Acquisition", color: "amber", icon: Target,
    features: [
      { title: "Reddit Intent Scanner",     desc: "Scans Reddit every 2 hours for posts expressing buying intent for your keywords. Each post is scored 1-10 by AI. Score ≥6 becomes a lead." },
      { title: "One-click Approve",         desc: "Approve a lead and they're instantly added as an active subscriber and tagged 'intent_lead'. Triggers any automation you've set up for that tag." },
      { title: "Lead Score Filtering",      desc: "Filter leads by score, platform, and keyword. See the exact Reddit post that triggered the detection." },
    ],
  },
  {
    id: "personalisation", label: "Personalisation", color: "purple", icon: Mail,
    features: [
      { title: "Subscriber Enrichment",     desc: "AI infers company name, job title, tech stack, recent funding, company news, and recent posts from the subscriber's email domain. Updates are stored and reused on every send." },
      { title: "9 Merge Tokens",            desc: "{{first_name}} {{full_name}} {{company}} {{title}} {{tech_stack}} {{funding}} {{company_news}} {{recent_post}} {{email}} — all replaced at send time, HTML-escaped." },
      { title: "Spintax",                   desc: "{Hi|Hello|Hey} {{first_name}} syntax randomises word choices per recipient. Prevents spam filters detecting identical send patterns across large lists." },
    ],
  },
  {
    id: "automation", label: "Automation", color: "blue", icon: GitBranch,
    features: [
      { title: "Visual Flow Builder",       desc: "Drag-and-drop canvas. Choose a trigger, add steps, set conditions. Runs automatically — no code required." },
      { title: "Behavioral Triggers",      desc: "Trigger automated emails from subscriber behavior: opens, link clicks, inactivity, campaign sends, and external webhooks from Shopify, Stripe, and Zapier.", href: "/behavioral-triggers" },
      { title: "6 Step Types",             desc: "Send Email, Wait, Condition (branch), LinkedIn Message, SMS (Twilio), Tag Subscriber. Chain any combination." },
      { title: "6 Triggers",              desc: "Subscriber Added, Tag Added, Campaign Sent, Email Opened, Link Clicked, Reply Received. Each trigger can run independent flows." },
      { title: "Condition Branching",      desc: "Split your flow based on email opened, link clicked, tag present, or custom metadata. Build complex sequences without any engineering." },
    ],
  },
  {
    id: "analytics", label: "Analytics & Optimisation", color: "teal", icon: BarChart3,
    features: [
      { title: "Campaign Analytics",        desc: "Opens, clicks, bounces, unsubscribes, and click-to-open rate per campaign. Sort any column. Click a campaign for per-day open timeline." },
      { title: "A/B Testing",              desc: "Test up to 4 subject line variants. Distribute recipients round-robin. Let the winning subject run to remaining sends." },
      { title: "Follow-up Campaigns",      desc: "One-click create a follow-up campaign targeting everyone who didn't open the original. AI rewrites the subject and first line automatically." },
      { title: "Auto-Optimization",        desc: "AI monitors campaigns, rewrites subject lines, and automatically resends to non-openers — boosting open rates without lifting a finger.", href: "/auto-optimization" },
      { title: "Send-Time Optimization",   desc: "AI analyzes 60 days of open history to find each subscriber's peak engagement hour and staggers delivery automatically.", href: "/send-time-optimization" },
      { title: "Engagement Scoring",       desc: "Automatic 0–100 engagement scoring for every subscriber based on opens, clicks, replies, recency, and frequency. Segment by hot, warm, and cold tiers.", href: "/engagement-scoring" },
      { title: "Revenue Attribution",      desc: "Track revenue generated by every email campaign. Shopify, Stripe, and manual conversion tracking with auto-attribution and daily trend dashboards.", href: "/revenue-attribution" },
    ],
  },
  {
    id: "network", label: "Merchant Network", color: "coral", icon: Store,
    features: [
      { title: "Offer Marketplace",         desc: "Browse partnership offers from other MoreMail merchants. Join an offer and you get a unique tracking link to embed in your campaigns." },
      { title: "Commission Tracking",       desc: "1×1 pixel fires on conversion. Platform logs the sale, calculates commission, credits your account. Real-time earnings dashboard." },
      { title: "Create Your Own Offers",    desc: "Set a commission rate, minimum audience size requirement, and offer description. Other merchants can browse and apply to partner." },
    ],
  },
];

const COLOR_MAP: Record<string, { badge: string; icon: string; bullet: string }> = {
  coral:  { badge: "bg-coral/10 text-coral",     icon: "bg-coral/10 text-coral",     bullet: "text-coral"   },
  green:  { badge: "bg-green-50 text-green-700",  icon: "bg-green-50 text-green-600",  bullet: "text-green-600"},
  amber:  { badge: "bg-amber-50 text-amber-700",  icon: "bg-amber-50 text-amber-600",  bullet: "text-amber-600"},
  purple: { badge: "bg-purple-50 text-purple-700",icon: "bg-purple-50 text-purple-600",bullet: "text-purple-600"},
  blue:   { badge: "bg-blue-50 text-blue-700",    icon: "bg-blue-50 text-blue-600",    bullet: "text-blue-600"  },
  teal:   { badge: "bg-teal/10 text-teal",        icon: "bg-teal/10 text-teal",        bullet: "text-teal"    },
};

export default function FeaturesPage() {
  return (
    <>
      <SeoHead
        title="Features — MoreMail | Every Feature You Need"
        description="AI campaign builder, email validation, warmup pool, custom tracking domains, spintax, intent lead finder, automation flows, and merchant network — all included."
        url={`${SITE_URL}/features`}
        breadcrumbs={[{name:"Home",url:SITE_URL},{name:"Features",url:`${SITE_URL}/features`}]}
      />

      {/* Hero */}
      <section className="bg-ink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-14 sm:pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] rounded-full text-white/70 text-xs font-bold mb-6">
            7 feature groups · 40+ capabilities
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4 leading-tight">
            Everything you need to send emails that actually get read
          </h1>
          <p className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            MoreMail includes features that other platforms charge add-on prices for — validation, warmup, custom tracking, and more.
          </p>
          <div className="flex flex-col items-center gap-4">
            <LeadCaptureBox />
            <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors flex items-center gap-1">
              View pricing <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick nav pills */}
      <div className="sticky top-[57px] sm:top-[61px] z-10 bg-paper/95 backdrop-blur-md border-b border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex gap-2 overflow-x-auto scrollbar-none">
          {SECTIONS.map((s) => {
            const c = COLOR_MAP[s.color];
            return (
              <a key={s.id} href={`#${s.id}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${c.badge} hover:opacity-80`}>
                <s.icon size={12} />
                {s.label}
              </a>
            );
          })}
        </div>
      </div>

      {/* Feature sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-16 sm:space-y-20">
        {SECTIONS.map((section) => {
          const c = COLOR_MAP[section.color];
          return (
            <section key={section.id} id={section.id}>
              <div className="flex items-center gap-3 mb-8">
                <div className={`p-2.5 rounded-xl ${c.icon}`}>
                  <section.icon size={20} />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink">{section.label}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {section.features.map((f) => (
                  <div key={f.title} className="bg-white rounded-2xl border border-black/[0.06] p-5 hover:shadow-md hover:shadow-black/[0.04] transition-shadow">
                    <div className="flex items-start gap-2 mb-2">
                      <Check size={15} className={`mt-0.5 flex-shrink-0 ${c.bullet}`} />
                      <h3 className="font-bold text-ink text-sm">{f.title}</h3>
                    </div>
                    <p className="text-slate text-sm leading-relaxed pl-[23px] mb-3">{f.desc}</p>
                    {(f as any).href && (
                      <div className="pl-[23px]">
                        <Link to={(f as any).href} className={`inline-flex items-center gap-1 text-xs font-semibold hover:underline ${c.bullet}`}>
                          Learn more <ArrowRight size={11} />
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <section className="bg-coral">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            Every feature. Every plan.
          </h2>
          <p className="text-white/80 text-base mb-8">No feature gates. No add-on pricing. Start free, scale when you need to.</p>
          <div className="flex justify-center">
            <LeadCaptureBox />
          </div>
        </div>
      </section>
    </>
  );
}
